import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Users as UsersIcon, UserCog, Pencil } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { RoleGate } from "../components/RoleGate";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { Modal } from "../components/Modal";
import { listUsers, createUser, updateUser, impersonateUser } from "../services/user.service";
import { getApiErrorMessage } from "../lib/apiClient";
import { useAuth } from "../hooks/useAuth";
import type { ManagedUser, UserRole } from "../types";

const ROLE_LABELS: Record<UserRole, string> = {
  SUPER_ADMIN: "Super Admin",
  EVENT_MANAGER: "Event Manager",
  EVENT_STAFF: "Event Staff",
};

function NewUserModal({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("EVENT_MANAGER");
  const [password, setPassword] = useState("");

  const mutation = useMutation({
    mutationFn: () => createUser({ email, name, role, password: password || undefined }),
    onSuccess: () => {
      toast.success("User created");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setEmail("");
      setName("");
      setPassword("");
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New User"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="user-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Creating..." : "Create User"}
          </button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="user-email">
            Google email
          </label>
          <input id="user-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="user-name">
            Name
          </label>
          <input id="user-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="user-role">
            Role
          </label>
          <select id="user-role" className="input" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="EVENT_MANAGER">Event Manager</option>
            <option value="EVENT_STAFF">Event Staff</option>
            <option value="SUPER_ADMIN">Super Admin</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="user-password">
            Password (optional)
          </label>
          <input
            id="user-password"
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            placeholder="Leave blank for Google sign-in only"
            autoComplete="new-password"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            If set (min 8 characters), this user can also log in with this email + password, in addition to Google.
          </p>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          To sign in with Google, the user's Google account email must match this email exactly. They cannot self-register.
        </p>
      </form>
    </Modal>
  );
}

function EditUserModal({ user, onClose }: { user: ManagedUser | null; onClose: () => void }): JSX.Element | null {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("EVENT_MANAGER");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name);
      setRole(user.role);
      setPassword("");
    }
  }, [user]);

  const mutation = useMutation({
    mutationFn: () => updateUser(user!.id, { name, role, ...(password ? { password } : {}) }),
    onSuccess: () => {
      toast.success("User updated");
      queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      title="Edit User"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="edit-user-form" className="btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : "Save"}
          </button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input className="input" value={user?.email ?? ""} disabled />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Email cannot be changed here.</p>
        </div>
        <div>
          <label className="label" htmlFor="edit-user-name">
            Name
          </label>
          <input id="edit-user-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div>
          <label className="label" htmlFor="edit-user-role">
            Role
          </label>
          <select id="edit-user-role" className="input" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="EVENT_MANAGER">Event Manager</option>
            <option value="EVENT_STAFF">Event Staff</option>
            <option value="SUPER_ADMIN">Super Admin</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="edit-user-password">
            Reset password (optional)
          </label>
          <input
            id="edit-user-password"
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            placeholder="Leave blank to keep current password"
            autoComplete="new-password"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Only fill this in to set/reset the password (min 8 characters). Leave blank to leave it unchanged.
          </p>
        </div>
      </form>
    </Modal>
  );
}

function UsersPageContent(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isImpersonating } = useAuth();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateUser(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const impersonateMutation = useMutation({
    mutationFn: (id: string) => impersonateUser(id),
    onSuccess: async (impersonatedUser) => {
      // The /impersonate response doesn't include who the admin was (only
      // the target user), so we can't seed auth/me client-side with the full
      // shape — drop everything and let it refetch for real from /auth/me,
      // which does return impersonatedBy.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      toast.success(`Now viewing as ${impersonatedUser.name}`);
      navigate("/dashboard");
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Users" }]} />
      <div className="flex items-center justify-between">
        <PageTitle icon={UsersIcon} title="Users" />
        <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
          New User
        </button>
      </div>

      <div className="mt-6">
        {isLoading && <LoadingSpinner />}
        {isError && <ErrorState message={getApiErrorMessage(error)} />}
        {data && (
          <div className="card overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-slate-50 dark:bg-slate-700/60">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-slate-500 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {data.map((u) => (
                  <tr
                    key={u.id}
                    className="cursor-pointer transition-colors duration-100 hover:bg-slate-100 dark:hover:bg-slate-700/60"
                    onClick={() => setEditingUser(u)}
                  >
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{u.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{ROLE_LABELS[u.role]}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${u.isActive ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300" : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>
                        {u.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
                          onClick={() => setEditingUser(u)}
                          title="Edit user"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        {u.role !== "SUPER_ADMIN" && u.isActive && !isImpersonating && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-800 disabled:opacity-50 dark:text-brand-400 dark:hover:text-brand-300 transition-colors duration-150"
                            onClick={() => impersonateMutation.mutate(u.id)}
                            disabled={impersonateMutation.isPending}
                            title={`View the app as ${u.name}`}
                          >
                            <UserCog className="h-3.5 w-3.5" />
                            Impersonate
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-sm font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
                          onClick={() => toggleActiveMutation.mutate({ id: u.id, isActive: !u.isActive })}
                        >
                          {u.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewUserModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} />
    </DashboardLayout>
  );
}

export default function UsersPage(): JSX.Element {
  return (
    <RoleGate roles={["SUPER_ADMIN"]}>
      <UsersPageContent />
    </RoleGate>
  );
}
